import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Trophy,
  Users,
  TrendingUp,
  Package,
  RefreshCw,
  ChevronDown,
  ArrowUp,
  ArrowDown,
  Crown,
  Medal,
  Award
} from 'lucide-react';
import { getCustomerLeaderboard } from '../../api/wholesale/analytics';
import { useFiscalPeriod } from '../../contexts/FiscalPeriodContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const CustomerLeaderboardPage = () => {
  const { periodRange } = useFiscalPeriod();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [sortBy, setSortBy] = useState('cartons');
  const [period, setPeriod] = useState('all');

  const periodOptions = [
    { value: 'all', label: 'All Time' },
    { value: 'year', label: 'This Year' },
    { value: 'quarter', label: 'This Quarter' },
    { value: 'month', label: 'This Month' },
    { value: 'week', label: 'This Week' }
  ];

  const sortOptions = [
    { value: 'cartons', label: 'By Cartons' },
    { value: 'value', label: 'By Value' },
    { value: 'orders', label: 'By Orders' }
  ];

  const fetchLeaderboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getCustomerLeaderboard({
        sort_by: sortBy,
        period,
        limit: 50
      });
      setLeaderboard(response.data || []);
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
      setError('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaderboard();
  }, [sortBy, period]);

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Crown className="w-6 h-6 text-yellow-500" />;
      case 2:
        return <Medal className="w-6 h-6 text-gray-400" />;
      case 3:
        return <Award className="w-6 h-6 text-amber-600" />;
      default:
        return (
          <span className="w-6 h-6 flex items-center justify-center text-gray-500 font-bold">
            {rank}
          </span>
        );
    }
  };

  const getRankBg = (rank) => {
    switch (rank) {
      case 1:
        return 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200';
      case 2:
        return 'bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200';
      case 3:
        return 'bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200';
      default:
        return 'bg-white border-gray-200';
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-emerald-500 rounded-xl text-white">
            <Trophy className="w-7 h-7" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Customer Performance Board</h1>
            <p className="text-gray-500 text-sm">Top performing customers by purchase volume</p>
          </div>
        </div>
        <button
          onClick={fetchLeaderboard}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-500 mb-1 uppercase">
              Sort By
            </label>
            <div className="relative">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full pl-3 pr-10 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 font-medium appearance-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                {sortOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-500 mb-1 uppercase">
              Time Period
            </label>
            <div className="relative">
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="w-full pl-3 pr-10 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 font-medium appearance-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                {periodOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Leaderboard */}
      {leaderboard.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
          <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Data Available</h3>
          <p className="text-gray-500">No customer purchase data found for the selected period.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {leaderboard.map((customer, index) => (
            <Link
              key={customer.distributor_id}
              to={`/wholesale/distributors/${customer.distributor_id}`}
              className={`block rounded-xl border p-4 hover:shadow-md transition-all ${getRankBg(customer.rank || index + 1)}`}
            >
              <div className="flex items-center gap-4">
                {/* Rank */}
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-white border border-gray-200 flex items-center justify-center shadow-sm">
                  {getRankIcon(customer.rank || index + 1)}
                </div>

                {/* Customer Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate">{customer.business_name}</h3>
                  <p className="text-sm text-gray-500 truncate">
                    {customer.contact_person_name || 'No contact'}
                  </p>
                </div>

                {/* Stats */}
                <div className="hidden sm:flex items-center gap-6">
                  <div className="text-center">
                    <div className="flex items-center gap-1 text-emerald-600">
                      <Package className="w-4 h-4" />
                      <span className="font-bold text-lg">
                        {parseInt(customer.total_cartons || 0).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">Cartons</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center gap-1 text-blue-600">
                      <TrendingUp className="w-4 h-4" />
                      <span className="font-bold text-lg">
                        ₦{(parseFloat(customer.total_value || 0) / 1000000).toFixed(1)}M
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">Value</p>
                  </div>
                  <div className="text-center">
                    <span className="font-bold text-lg text-gray-700">{customer.total_orders}</span>
                    <p className="text-xs text-gray-500">Orders</p>
                  </div>
                </div>

                {/* Mobile Stats */}
                <div className="sm:hidden text-right">
                  <p className="font-bold text-emerald-600">
                    {parseInt(customer.total_cartons || 0).toLocaleString()} ctns
                  </p>
                  <p className="text-sm text-gray-500">
                    ₦{(parseFloat(customer.total_value || 0) / 1000000).toFixed(1)}M
                  </p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomerLeaderboardPage;
